import { useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/site";

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % heroSlides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, []);

  const go = (idx: number) => setCurrent((idx + heroSlides.length) % heroSlides.length);

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      go(diff < 0 ? current + 1 : current - 1);
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[560px] md:h-[680px] overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {heroSlides.map((slide, idx) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            idx === current ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} - 芒果传媒`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/45 to-foreground-950/80"></div>
        </div>
      ))}

      <div className="relative z-10 h-full flex items-center">
        <div className="container-site w-full">
          <div key={current} className="max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary-500/90 text-foreground-950 text-xs md:text-sm font-semibold tracking-wide">
              <i className="ri-movie-2-line"></i>
              {heroSlides[current].eyebrow}
            </span>
            <h1 className="font-heading font-extrabold text-3xl md:text-6xl text-background-50 leading-tight mt-5">
              {heroSlides[current].title}
            </h1>
            <p className="text-background-200 text-sm md:text-lg mt-4 leading-relaxed max-w-2xl">
              {heroSlides[current].subtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-8">
              <Link
                to={heroSlides[current].link}
                className="px-6 py-3 rounded-md bg-primary-500 text-foreground-950 font-semibold hover:bg-primary-600 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                查看代表作品
              </Link>
              <Link
                to="/services"
                className="px-6 py-3 rounded-md border border-background-200/70 text-background-50 font-semibold hover:bg-background-50/10 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                了解我们的服务
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-0 right-0 z-10">
        <div className="container-site flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {heroSlides.map((s, idx) => (
              <button
                key={s.id}
                type="button"
                aria-label={`切换到第 ${idx + 1} 张`}
                onClick={() => go(idx)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  idx === current ? "w-8 bg-primary-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
                }`}
              ></button>
            ))}
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              aria-label="上一张"
              onClick={() => go(current - 1)}
              className="w-10 h-10 rounded-full border border-background-200/50 flex items-center justify-center text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <button
              type="button"
              aria-label="下一张"
              onClick={() => go(current + 1)}
              className="w-10 h-10 rounded-full border border-background-200/50 flex items-center justify-center text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}